'use client';

import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@ui/lib/utils';
import { Button } from '@ui/primitives/Button';
import { Icon } from '@ui/primitives/Icon';

interface TextInplaceProps {
  value: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
  onSave?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function TextInplace({
  value: initialValue,
  placeholder = '',
  onChange,
  onValueChange,
  onSave,
  disabled = false,
  className,
}: TextInplaceProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    if (value !== initialValue) {
      if (onSave) {
        onSave(value);
      }
      if (onValueChange) {
        onValueChange(value);
      }
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'flex-1 px-3 py-1 border border-gray-500 rounded-md focus:outline-none focus:ring-gray-500',
            className
          )}
        />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleSave}
          icon={<Icon name="fa-solid fa-check" size="sm" />}
          className="shrink-0"
        />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          icon={<Icon name="fa-solid fa-xmark" size="sm" />}
          className="shrink-0"
        />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group relative inline-flex items-center gap-2 px-3 py-2 rounded-md transition-colors',
        !disabled && 'cursor-pointer hover:bg-gray-100',
        disabled && 'cursor-not-allowed opacity-50',
        !value && 'text-gray-400',
        className
      )}
    >
      <span className="flex-1">
        {value || placeholder}
      </span>
      {!disabled && isHovered && (
        <Icon
          name="fa-solid fa-pen"
          size="sm"
          className="text-gray-400 group-hover:text-gray-600 transition-colors"
        />
      )}
    </div>
  );
}
